(function () {
  const STORAGE_KEY = 'craft_cellars_selected_location';

  function getConfig() {
    return window.craftCellarsLocationConfig || null;
  }

  /*
   * localStorage key: craft_cellars_selected_location
   *
   * Persistence (localStorage) is ONLY allowed while the Store Locator Modal is enabled.
   * Shoppers must be able to change store again via Change Store / modal — otherwise a
   * saved value would trap them with no UI to switch back.
   *
   * | Modal | PDP  | localStorage | Behavior |
   * | ON    | ON   | Yes          | Shared selection: modal/header + PDP switcher |
   * | ON    | OFF  | Yes          | Modal/header own selection |
   * | OFF   | ON   | No (cleared) | PDP uses defaultLocationKey; switcher is in-memory
   * |       |      |              | for this page only (not written to localStorage) |
   * | OFF   | OFF  | No (cleared) | No location side effects |
   */
  let memorySelection = null;

  function isPersistenceEnabled() {
    return getConfig()?.enabled === true;
  }

  function clearPersistedSelection() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      // Ignore storage access errors (private mode, blocked storage, etc.)
    }
    delete document.documentElement.dataset.craftCellarsLocation;
  }

  function clearMemorySelection() {
    memorySelection = null;
  }

  function getStoredSelection() {
    if (isPersistenceEnabled()) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (e) {
        return null;
      }
    }

    // Modal off: never read localStorage. Allow in-memory choice from PDP switcher only.
    return memorySelection;
  }

  function setStoredSelection(selection) {
    if (!selection?.key) return;

    if (isPersistenceEnabled()) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
      } catch (e) {
        // Ignore storage write failures; still broadcast selection below.
      }
      memorySelection = null;
      document.documentElement.dataset.craftCellarsLocation = selection.key;
    } else {
      // Modal off: keep switcher usable on this page, but do not trap the shopper in storage.
      clearPersistedSelection();
      memorySelection = selection;
    }

    window.CraftCellarsStorefrontApi?.clearCache?.();
    document.dispatchEvent(
      new CustomEvent('craft-cellars:location-selected', { detail: selection, bubbles: true })
    );
  }

  function syncPersistenceState() {
    if (!isPersistenceEnabled()) {
      clearPersistedSelection();
      // Fresh page load with modal off should not revive a prior in-memory pick from a
      // previous navigation within the same JS context (theme editor section reloads).
      if (!document.querySelector('location-inventory')) {
        clearMemorySelection();
      }
    }
  }

  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore storage access errors (private mode, blocked storage, etc.)
  }

  syncPersistenceState();

  window.CraftCellarsLocation = {
    storageKey: STORAGE_KEY,
    isPersistenceEnabled,
    getSelection: getStoredSelection,
    setSelection: setStoredSelection,
    clearSelection() {
      clearPersistedSelection();
      clearMemorySelection();
    },
    syncPersistenceState,

    syncHeader() {
      if (!isPersistenceEnabled()) return;

      const selection = getStoredSelection();
      if (!selection) return;

      document.querySelectorAll('[data-location-picker-trigger]').forEach(button => {
        const isSelected = button.dataset.locationKey === selection.key;

        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', isSelected);

        const address = button.querySelector('[data-location-address]');
        if (address && isSelected) {
          address.textContent = address.textContent;
        }
      });
    },
  };

  document.addEventListener('DOMContentLoaded', syncPersistenceState);
  document.addEventListener('shopify:section:load', syncPersistenceState);

  if (!customElements.get('location-selector-modal')) {
    customElements.define(
      'location-selector-modal',
      class LocationSelectorModal extends HTMLElement {
        constructor() {
          super();
          this.onShopClick = this.onShopClick.bind(this);
          this.onKeyUp = this.onKeyUp.bind(this);
          this.onSliderPrev = this.onSliderPrev.bind(this);
          this.onSliderNext = this.onSliderNext.bind(this);
          this.updateSliderState = this.updateSliderState.bind(this);
          this.onLocationSelected = this.onLocationSelected.bind(this);
          this.onPageShow = this.onPageShow.bind(this);
        }

        connectedCallback() {
          this.config = window.craftCellarsLocationConfig;
          if (!this.config?.enabled) return;

          this.overlay = this;
          this.dialog = this.querySelector('.location-selector-modal__dialog');
          this.closeButton = this.querySelector('.location-selector-modal__close');
          this.grid = this.querySelector('.location-selector-modal__grid');
          this.prevButton = this.querySelector('[data-location-slider-prev]');
          this.nextButton = this.querySelector('[data-location-slider-next]');
          this.pagination = this.querySelector('.location-selector-modal__pagination');
          this.cards = Array.from(this.querySelectorAll('[data-location-key]'));

          this.cards.forEach(card => {
            card.querySelector('[data-shop-now]')?.addEventListener('click', this.onShopClick);
          });

          if (this.closeButton) {
            this.closeButton.hidden = !(this.config.allowDismiss || getStoredSelection());
            this.closeButton.addEventListener('click', () => this.hide());
          }

          this.prevButton?.addEventListener('click', this.onSliderPrev);
          this.nextButton?.addEventListener('click', this.onSliderNext);
          this.grid?.addEventListener('scroll', this.updateSliderState, { passive: true });
          window.addEventListener('resize', this.updateSliderState);
          window.addEventListener('pageshow', this.onPageShow);
          document.addEventListener('craft-cellars:location-selected', this.onLocationSelected);

          this.syncSelectedCards();
          this.createPagination();
          this.updateSliderState();
          this.maybeShow();
        }

        disconnectedCallback() {
          this.prevButton?.removeEventListener('click', this.onSliderPrev);
          this.nextButton?.removeEventListener('click', this.onSliderNext);
          this.grid?.removeEventListener('scroll', this.updateSliderState);
          window.removeEventListener('resize', this.updateSliderState);
          window.removeEventListener('pageshow', this.onPageShow);
          document.removeEventListener('craft-cellars:location-selected', this.onLocationSelected);
        }

        onPageShow(event) {
          if (!event.persisted) return;
          this.maybeShow();
        }

        syncSelectedCards(selection = getStoredSelection()) {
          const activeKey = selection?.key || this.config.defaultLocationKey;
          this.cards.forEach(card => {
            const isSelected = card.dataset.locationKey === activeKey;
            card.classList.toggle('is-selected', isSelected);
            card.classList.toggle(
              'is-default',
              !selection && card.dataset.locationKey === this.config.defaultLocationKey
            );
          });
        }

        onLocationSelected(event) {
          this.syncSelectedCards(event.detail || getStoredSelection());
        }

        maybeShow() {
          const stored = getStoredSelection();

          if (stored) {
            document.documentElement.dataset.craftCellarsLocation = stored.key;
            window.CraftCellarsLocation?.syncHeader?.();
          }

          if (this.config.forceShowInEditor || this.config.isHomepage || !stored) {
            this.show();
          }
        }

        show() {
          const selection = getStoredSelection();
          const focusKey = selection?.key || this.config.defaultLocationKey;
          this.syncSelectedCards(selection);
          if (this.closeButton) {
            this.closeButton.hidden = !(this.config.allowDismiss || selection);
          }
          document.body.classList.add('overflow-hidden');
          this.setAttribute('open', '');
          this.addEventListener('keyup', this.onKeyUp);
          const focusTarget =
            this.querySelector(`[data-location-key="${focusKey}"] [data-shop-now]`) ||
            this.querySelector('[data-shop-now]');
          if (typeof trapFocus === 'function') trapFocus(this.dialog, focusTarget);
          this.updateSliderState();
        }

        hide() {
          if (!this.config.allowDismiss && !getStoredSelection()) return;
          document.body.classList.remove('overflow-hidden');
          document.body.classList.remove('overflow-hidden-tablet');
          this.removeAttribute('open');
          this.removeEventListener('keyup', this.onKeyUp);
          if (typeof removeTrapFocus === 'function') removeTrapFocus();
        }

        onKeyUp(event) {
          if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        }

        isMobileSlider() {
          return window.matchMedia('(max-width: 989px)').matches;
        }

        getSlideOffset() {
          if (!this.cards.length) return 0;
          const cardWidth = this.cards[0].getBoundingClientRect().width;
          const styles = window.getComputedStyle(this.grid);
          const gap = parseFloat(styles.columnGap || styles.gap || '0');
          return cardWidth + gap;
        }

        createPagination() {
          if (!this.pagination || !this.cards.length) return;
          this.pagination.innerHTML = '';

          this.cards.forEach((_, index) => {
            const dot = document.createElement('button');
            dot.type = 'button';
            dot.className = 'location-selector-modal__pagination-dot';
            dot.dataset.index = String(index);
            dot.setAttribute('aria-label', `Go to location ${index + 1}`);
            dot.addEventListener('click', () => {
              if (!this.isMobileSlider()) return;
              this.grid.scrollTo({ left: index * this.getSlideOffset(), behavior: 'smooth' });
            });
            this.pagination.appendChild(dot);
          });
          this.updatePagination();
        }

        updatePagination() {
          if (!this.pagination || !this.cards.length) return;

          const dots = Array.from(this.pagination.children);
          if (!this.isMobileSlider()) {
            dots.forEach(dot => dot.classList.remove('is-active'));
            return;
          }

          const current = this.grid.scrollLeft;
          const offset = this.getSlideOffset() || 1;
          const rawIndex = Math.round(current / offset);
          const maxIndex = this.cards.length - 1;
          const activeIndex = Math.min(maxIndex, Math.max(0, rawIndex));

          dots.forEach((dot, index) => {
            dot.classList.toggle('is-active', index === activeIndex);
          });
        }

        onSliderPrev() {
          if (!this.grid || !this.isMobileSlider()) return;
          this.grid.scrollBy({ left: -this.getSlideOffset(), behavior: 'smooth' });
        }

        onSliderNext() {
          if (!this.grid || !this.isMobileSlider()) return;
          this.grid.scrollBy({ left: this.getSlideOffset(), behavior: 'smooth' });
        }

        updateSliderState() {
          if (!this.grid || !this.prevButton || !this.nextButton) return;

          if (!this.isMobileSlider()) {
            this.prevButton.disabled = true;
            this.nextButton.disabled = true;
            this.updatePagination();
            return;
          }

          const maxScroll = this.grid.scrollWidth - this.grid.clientWidth;
          const current = this.grid.scrollLeft;
          const threshold = 6;
          this.prevButton.disabled = current <= threshold;
          this.nextButton.disabled = current >= maxScroll - threshold;
          this.updatePagination();
        }

        onShopClick(event) {
          const card = event.currentTarget.closest('[data-location-key]');
          const cardUrl = card ? card.querySelector('[data-shop-now]')?.href : null;

          if (!card) return;
          if (card.dataset.locationKey === 'location_3') {
            this.hide();
            return;
          }

          const selection = {
            key: card.dataset.locationKey,
            type: card.dataset.locationType,
            id: card.dataset.locationId,
            name: card.dataset.locationName,
            address: card.dataset.locationAddress,
          };

          setStoredSelection(selection);

          document.querySelectorAll('[data-location-picker-trigger]').forEach(button => {
            const isSelected = button.dataset.locationKey === selection.key;

            button.classList.toggle('is-selected', isSelected);
            button.setAttribute('aria-pressed', isSelected);

            const address = button.querySelector('[data-location-address]');
            if (address && isSelected) {
              address.textContent = address.textContent;
            }
          });

          this.cards.forEach(c => c.classList.toggle('is-selected', c === card));
          window.CraftCellarsLocation?.syncHeader();
          this.hide();

          if (this.config.isHomepage && cardUrl === window.location.href) {
            event.preventDefault();
          }
        }
      }
    );
  }
})();
